import {useMemo, useState} from 'react'
import {Button, Spinner, Table, Modal, Form, Badge} from 'react-bootstrap'
import {useQuery, useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {PERMISSION_CATALOG, ROLES, CREATE_ROLE, UPDATE_ROLE, DELETE_ROLE} from '../../../../gql/authz'
import {defineMessages, FormattedMessage, useIntl} from 'react-intl'

const M = defineMessages({
  roleCount: {id: 'admin.roles.count', defaultMessage: '{count, plural, one {# role} other {# roles}}'},
  newRole: {id: 'admin.roles.action.new', defaultMessage: 'New role'},
  thRole: {id: 'admin.roles.th.role', defaultMessage: 'Role'},
  thPermissions: {id: 'admin.roles.th.permissions', defaultMessage: 'Permissions'},
  permCount: {id: 'admin.roles.permCount', defaultMessage: '{count} / {total}'},
  editPermissions: {id: 'admin.roles.action.editPermissions', defaultMessage: 'Edit permissions'},
  rename: {id: 'admin.roles.action.rename', defaultMessage: 'Rename'},
  reassignBeforeDelete: {
    id: 'admin.roles.action.reassignBeforeDelete',
    defaultMessage: 'Reassign members before deleting',
  },
  empty: {id: 'admin.roles.empty', defaultMessage: 'No roles yet. Create one to start.'},
  modalNew: {id: 'admin.roles.modal.new', defaultMessage: 'New role'},
  modalRename: {id: 'admin.roles.modal.rename', defaultMessage: 'Rename role'},
  roleName: {id: 'admin.roles.field.name', defaultMessage: 'Role name'},
  roleNamePlaceholder: {id: 'admin.roles.field.namePlaceholder', defaultMessage: 'e.g. Sales Supervisor'},
  create: {id: 'admin.roles.action.create', defaultMessage: 'Create'},
  toastCreated: {id: 'admin.roles.toast.created', defaultMessage: 'Role "{name}" created.'},
  toastRenamed: {id: 'admin.roles.toast.renamed', defaultMessage: 'Role renamed.'},
  toastDeleted: {id: 'admin.roles.toast.deleted', defaultMessage: 'Role "{name}" deleted.'},
  confirmDeleteIrreversible: {
    id: 'admin.roles.confirm.deleteIrreversible',
    defaultMessage: 'Delete role "{name}"? This cannot be undone.',
  },
  toastSaveFailed: {id: 'admin.roles.toast.saveFailed', defaultMessage: 'Could not save the role'},
  toastDeleteFailed: {id: 'admin.roles.toast.deleteFailed', defaultMessage: 'Could not delete the role'},
  toastHasMembers: {
    id: 'admin.roles.toast.hasMembers',
    defaultMessage: '"{name}" has {count, plural, one {# member} other {# members}}. Reassign them first.',
  },
})

type Role = {id: string; name: string; member_count: number; permissions: string[]}

// Roles management: a clear list of roles with member/permission counts and
// explicit Create / Edit permissions / Rename / Delete actions.
export const RolesManager = ({onEditRole}: {onEditRole: (id: string) => void}) => {
  const intl = useIntl()
  const {data, loading, refetch} = useQuery(ROLES, {fetchPolicy: 'cache-and-network'})
  const {data: catData} = useQuery(PERMISSION_CATALOG, {fetchPolicy: 'cache-first'})
  const [createRole, {loading: creating}] = useMutation(CREATE_ROLE)
  const [updateRole, {loading: renaming}] = useMutation(UPDATE_ROLE)
  const [deleteRole] = useMutation(DELETE_ROLE)

  const roles: Role[] = useMemo(() => data?.roles ?? [], [data])

  // Catalog codenames + total, to show "X / Y permissions" (ignoring the noisy
  // built-in Django perms that admins accumulate).
  const catalog = useMemo(() => {
    const set = new Set<string>()
    let total = 0
    ;(catData?.permissionCatalog?.modules ?? []).forEach((m: any) =>
      m.permissions.forEach((p: any) => {
        set.add(p.codename)
        total += 1
      })
    )
    return {set, total}
  }, [catData])

  const [modal, setModal] = useState<{mode: 'create' | 'rename'; id?: string; name: string} | null>(null)

  const submitModal = async () => {
    const name = modal?.name.trim()
    if (!name) return
    try {
      if (modal!.mode === 'create') {
        const {data: r} = await createRole({variables: {name}})
        if (r?.createRole?.success) toast.success(intl.formatMessage(M.toastCreated, {name}))
      } else {
        const {data: r} = await updateRole({variables: {roleId: modal!.id, name}})
        if (r?.updateRole?.success) toast.success(intl.formatMessage(M.toastRenamed))
      }
      setModal(null)
      await refetch()
    } catch (err: any) {
      toast.error(err?.message || intl.formatMessage(M.toastSaveFailed))
    }
  }

  const handleDelete = async (r: Role) => {
    if (r.member_count > 0) {
      toast.warning(intl.formatMessage(M.toastHasMembers, {name: r.name, count: r.member_count}))
      return
    }
    if (!window.confirm(intl.formatMessage(M.confirmDeleteIrreversible, {name: r.name}))) return
    try {
      const {data: res} = await deleteRole({variables: {roleId: r.id}})
      if (res?.deleteRole?.success) {
        toast.success(intl.formatMessage(M.toastDeleted, {name: r.name}))
        await refetch()
      }
    } catch (err: any) {
      toast.error(err?.message || intl.formatMessage(M.toastDeleteFailed))
    }
  }

  return (
    <div>
      <div className='d-flex align-items-center justify-content-between mb-3'>
        <span className='text-muted fs-7'>
          <FormattedMessage {...M.roleCount} values={{count: roles.length}} />
        </span>
        <Button variant='primary' size='sm' onClick={() => setModal({mode: 'create', name: ''})}>
          <i className='bi bi-plus fs-5' /> <FormattedMessage {...M.newRole} />
        </Button>
      </div>

      {loading && !roles.length ? (
        <div className='py-10 text-center'><Spinner animation='border' /></div>
      ) : (
        <Table hover responsive className='align-middle'>
          <thead className='table-light'>
            <tr className='fw-bold text-muted'>
              <th className='ps-4'>
                <FormattedMessage {...M.thRole} />
              </th>
              <th className='text-center'>
                <FormattedMessage id='common.field.members' defaultMessage='Members' />
              </th>
              <th className='text-center'>
                <FormattedMessage {...M.thPermissions} />
              </th>
              <th className='text-end pe-4'>
                <FormattedMessage id='common.field.actions' defaultMessage='Actions' />
              </th>
            </tr>
          </thead>
          <tbody>
            {roles.map((r) => {
              const permCount = r.permissions.filter((c) => catalog.set.has(c)).length
              return (
                <tr key={r.id}>
                  <td className='ps-4 fw-bold'>{r.name}</td>
                  <td className='text-center'>
                    <Badge bg='light' text='dark'>{r.member_count}</Badge>
                  </td>
                  <td className='text-center'>
                    <span className={`badge ${permCount === 0 ? 'badge-light' : permCount === catalog.total ? 'badge-light-success' : 'badge-light-primary'}`}>
                      {catalog.total
                        ? intl.formatMessage(M.permCount, {count: permCount, total: catalog.total})
                        : permCount}
                    </span>
                  </td>
                  <td className='text-end pe-4'>
                    <div className='d-flex justify-content-end gap-2'>
                      <Button variant='light-primary' size='sm' onClick={() => onEditRole(r.id)}>
                        <i className='bi bi-sliders me-1' /> <FormattedMessage {...M.editPermissions} />
                      </Button>
                      <Button
                        variant='light'
                        size='sm'
                        title={intl.formatMessage(M.rename)}
                        onClick={() => setModal({mode: 'rename', id: r.id, name: r.name})}
                      >
                        <i className='bi bi-pencil' />
                      </Button>
                      <Button
                        variant='light-danger'
                        size='sm'
                        title={
                          r.member_count
                            ? intl.formatMessage(M.reassignBeforeDelete)
                            : intl.formatMessage({id: 'common.action.delete', defaultMessage: 'Delete'})
                        }
                        onClick={() => handleDelete(r)}
                      >
                        <i className='bi bi-trash' />
                      </Button>
                    </div>
                  </td>
                </tr>
              )
            })}
            {roles.length === 0 && (
              <tr>
                <td colSpan={4} className='text-center text-muted py-6'>
                  <FormattedMessage {...M.empty} />
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      <Modal show={!!modal} onHide={() => setModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {modal?.mode === 'create' ? (
              <FormattedMessage {...M.modalNew} />
            ) : (
              <FormattedMessage {...M.modalRename} />
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Label className='small text-muted'>
            <FormattedMessage {...M.roleName} />
          </Form.Label>
          <Form.Control
            autoFocus
            value={modal?.name ?? ''}
            onChange={(e) => setModal((m) => (m ? {...m, name: e.target.value} : m))}
            onKeyDown={(e) => e.key === 'Enter' && submitModal()}
            placeholder={intl.formatMessage(M.roleNamePlaceholder)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant='light' onClick={() => setModal(null)}>
            <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
          </Button>
          <Button variant='primary' onClick={submitModal} disabled={creating || renaming || !modal?.name.trim()}>
            {creating || renaming ? (
              <Spinner animation='border' size='sm' />
            ) : modal?.mode === 'create' ? (
              <FormattedMessage {...M.create} />
            ) : (
              <FormattedMessage id='common.action.save' defaultMessage='Save' />
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
